import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

type Site =
  | "datadoghq.com"
  | "datadoghq.eu"
  | "us3.datadoghq.com"
  | "us5.datadoghq.com"
  | "ap1.datadoghq.com"
  | "ddog-gov.com";

interface DatadogEvent {
  type?: string;
  resource?: {
    url: string;
  };
}

interface DatadogLog {
  message?: string;
}

const DEFAULT_SESSION_SAMPLE_RATE = 100;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 20;

const datadogConfig = {
  clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
  site: process.env.NEXT_PUBLIC_DATADOG_SITE || "datadoghq.com",
  service: process.env.NEXT_PUBLIC_DATADOG_SERVICE || "d2c-scott",
  env:
    process.env.NEXT_PUBLIC_DATADOG_ENV ||
    process.env.NODE_ENV ||
    "development",
  version:
    process.env.NEXT_PUBLIC_DATADOG_VERSION ||
    process.env.npm_package_version ||
    "1.0.0",
  sessionSampleRate:
    Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE) ||
    DEFAULT_SESSION_SAMPLE_RATE,
  sessionReplaySampleRate:
    Number(process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE) ||
    DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
  trackUserInteractions:
    process.env.NEXT_PUBLIC_DATADOG_TRACK_USER_INTERACTIONS !== "false",
  trackResources: process.env.NEXT_PUBLIC_DATADOG_TRACK_RESOURCES !== "false",
  trackLongTasks: process.env.NEXT_PUBLIC_DATADOG_TRACK_LONG_TASKS !== "false",
  defaultPrivacyLevel:
    (process.env.NEXT_PUBLIC_DATADOG_DEFAULT_PRIVACY_LEVEL as
      | "allow"
      | "mask"
      | "mask-user-input") || "mask-user-input",
};

export const initializeDatadog = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  if (!(datadogConfig.clientToken && datadogConfig.applicationId)) {
    return false;
  }

  try {
    datadogRum.init({
      applicationId: datadogConfig.applicationId,
      clientToken: datadogConfig.clientToken,
      site: datadogConfig.site as Site,
      service: datadogConfig.service,
      env: datadogConfig.env,
      version: datadogConfig.version,
      sessionSampleRate: datadogConfig.sessionSampleRate,
      sessionReplaySampleRate: datadogConfig.sessionReplaySampleRate,
      trackUserInteractions: datadogConfig.trackUserInteractions,
      trackResources: datadogConfig.trackResources,
      trackLongTasks: datadogConfig.trackLongTasks,
      defaultPrivacyLevel: datadogConfig.defaultPrivacyLevel,
      beforeSend: (event: unknown) => {
        const typedEvent = event as DatadogEvent;
        if (
          typedEvent.type === "resource" &&
          typedEvent.resource?.url.includes("api/auth")
        ) {
          return false;
        }

        return true;
      },
    });

    datadogLogs.init({
      clientToken: datadogConfig.clientToken,
      site: datadogConfig.site as Site,
      service: datadogConfig.service,
      env: datadogConfig.env,
      version: datadogConfig.version,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["error"],
      sessionSampleRate: datadogConfig.sessionSampleRate,
      beforeSend: (log: unknown) => {
        const typedLog = log as DatadogLog;
        if (
          typedLog.message?.includes("password") ||
          typedLog.message?.includes("token")
        ) {
          return false;
        }

        return true;
      },
    });

    datadogRum.startSessionReplayRecording();
    return true;
  } catch (_error) {
    // Datadog initialization must never affect the storefront.
    return false;
  }
};

export const browserLogger = {
  info: (message: string, meta?: object) => {
    datadogLogs.logger.info(message, meta);
  },

  error: (message: string, error?: Error, meta?: object) => {
    if (error instanceof Error) {
      datadogLogs.logger.error(message, {
        ...meta,
        error: error.message,
        stack: error.stack,
      });
      datadogRum.addError(error, { ...meta, source: "logger" });
    } else {
      datadogLogs.logger.error(message, { ...meta, error });
    }
  },

  warn: (message: string, meta?: object) => {
    datadogLogs.logger.warn(message, meta);
  },

  debug: (message: string, meta?: object) => {
    datadogLogs.logger.debug(message, meta);
  },

  performance: (operation: string, duration: number, meta?: object) => {
    datadogLogs.logger.info(
      `Performance: ${operation} completed in ${Math.round(duration)}ms`,
      {
        ...meta,
        performance: {
          operation,
          duration_ms: Math.round(duration),
        },
      }
    );
    datadogRum.addTiming(operation, duration);
  },

  userAction: (action: string, userId?: string, meta?: object) => {
    datadogLogs.logger.info(`User action: ${action}`, {
      ...meta,
      user: userId ? { id: userId } : undefined,
      action: {
        type: action,
        timestamp: new Date().toISOString(),
      },
    });
    datadogRum.addAction(action, { ...meta, userId });
  },

  businessMetric: (
    metric: string,
    value: number,
    tags?: Record<string, string>
  ) => {
    datadogLogs.logger.info(`Business metric: ${metric} = ${value}`, {
      metric: {
        name: metric,
        value,
        tags,
      },
    });
    datadogRum.setGlobalContextProperty(`metric_${metric}`, value);
  },

  rum: {
    addAction: (name: string, context?: object) => {
      datadogRum.addAction(name, context);
    },

    addError: (error: Error | string, context?: object) => {
      if (typeof error === "string") {
        datadogRum.addError(new Error(error), context);
      } else {
        datadogRum.addError(error, context);
      }
    },

    addTiming: (name: string, time?: number) => {
      datadogRum.addTiming(name, time);
    },

    setUser: (user: { id?: string; name?: string; email?: string }) => {
      datadogRum.setUser(user);
    },

    setGlobalContextProperty: (key: string, value: unknown) => {
      datadogRum.setGlobalContextProperty(key, value);
    },
  },
};
